import { useEffect, useState } from "react";

import { ITenant, TenantRegion } from "../../../@types/admin/AdminHospital";
import { useUpdateTenant } from "../../../hooks/mutations/admin/hospitals";
import { useToast } from "../../../hooks/useToast";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  AdminSelectField,
  FieldError,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  FormGrid,
  ReadOnlyValue,
  SectionActions,
  SectionCaption,
  SectionCard,
  SectionHeader,
  SectionHeading,
  SectionTitle,
} from "../shared";

interface ITenantRecordFormProps {
  tenant: ITenant;
}

const REGIONS: TenantRegion[] = ["EU", "US"];
const MAX_NAME_LENGTH = 100;

function isTenantRegion(value: string): value is TenantRegion {
  return REGIONS.includes(value as TenantRegion);
}

/**
 * Tenant record form — Retool's `EditTenant` on the details page (EN-5154),
 * the same two editable fields the Tenants list modal offers.
 */
export function TenantRecordForm({ tenant }: ITenantRecordFormProps) {
  const { showToast } = useToast();
  const { updateTenant, isUpdatingTenant } = useUpdateTenant();

  const [name, setName] = useState(tenant.name);
  const [region, setRegion] = useState<string>(tenant.region);

  useEffect(() => {
    setName(tenant.name);
    setRegion(tenant.region);
  }, [tenant]);

  const nameError = name.trim() ? undefined : "Name is required.";
  const regionChanged = region !== tenant.region;

  const handleSave = async () => {
    if (nameError || !isTenantRegion(region)) return;

    try {
      await updateTenant({ id: tenant.id, name: name.trim(), region });
      showToast("Successfully updated tenant details", "success");
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error updating the tenant details."
        ),
        "warning"
      );
    }
  };

  return (
    <SectionCard>
      <SectionHeader>
        <SectionHeading>
          <SectionTitle>Tenant details</SectionTitle>
          <SectionCaption>ID {tenant.id}</SectionCaption>
        </SectionHeading>
      </SectionHeader>

      <FormGrid>
        <FilterGroup>
          <FilterLabel htmlFor="tenant-detail-name">Name</FilterLabel>
          <FilterInput
            id="tenant-detail-name"
            type="text"
            maxLength={MAX_NAME_LENGTH}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {nameError && <FieldError>{nameError}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="tenant-detail-region">Region</FilterLabel>
          <AdminSelectField
            id="tenant-detail-region"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
          >
            {REGIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </AdminSelectField>
          {regionChanged ? (
            <FieldError>
              Changing the region does not move existing data — the tenant
              &apos;s records stay in {tenant.region}.
            </FieldError>
          ) : (
            <FieldHint>Which regional database this tenant lives in.</FieldHint>
          )}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Created</FilterLabel>
          {/* The Tenants list beside it already formats this one properly;
              slicing the ISO string to ten characters printed the UTC date,
              which is the wrong day for anyone west of UTC late in the
              evening, and dropped the time entirely. */}
          <ReadOnlyValue>
            {getLongFormattedDate(tenant.created, "-")}
          </ReadOnlyValue>
        </FilterGroup>
      </FormGrid>

      <SectionActions>
        <FilterButton
          variant="primary"
          disabled={isUpdatingTenant || Boolean(nameError)}
          onClick={handleSave}
        >
          {isUpdatingTenant ? "Saving..." : "Save"}
        </FilterButton>
      </SectionActions>
    </SectionCard>
  );
}
